import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HasAnalytics, HasPageTitleAndMetaData, StaticPageInterface } from '@app/interfaces/_index';
import {
  GoogleAnalyticsService,
  QuickStartGuideService,
  TrainingSupportElearningPageService,
} from '@app/services/_index';
import { getPageIdBySlug, logGoogleAnalytics } from '@app/utilities/_index';

import { ActivatedRoute } from '@angular/router';
import { StaticPageAdapter } from '@app/adapters/_index';
import { StaticPage } from '@app/models/_index';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-training-support-elearning-page',
  templateUrl: './training-support-elearning-page.component.html',
  styleUrls: ['./training-support-elearning-page.component.scss'],
  standalone: false,
})
export class TrainingSupportElearningPageComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, HasAnalytics
{
  // Page Properties
  isLoading: boolean;

  // The training support eLearning pages - Genesys Cloud ELearning, Genesys DX Elearning and Genesys BeyondCX Elearning
  trainingSupportElearningPage: StaticPage[] = [];

  // Assign navigation items - links to control the left side body content and display
  parentNavItems: StaticPage[] = []; // Parent nav items
  childNavItems: StaticPage[] = []; // Child nav items

  // Shared common trainins support heading and short description on the body
  trainingSupportHeading: StaticPage;
  subs: Subscription[] = [];
  slugName: string;

  constructor(
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    private trainingSupportElearningPageService: TrainingSupportElearningPageService,
    private quickStartGuideService: QuickStartGuideService,
    private staticPageAdapter: StaticPageAdapter,
    private titleService: Title,
    private metaService: Meta
  ) {}

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * On Init, call getQuickStartGuide and Dynamic eLearning Content to load data
   * These include Genesys Cloud eLearning, Genesys DX elearning, and Genesys BeyondCX FAQs.
   */
  ngOnInit(): void {
    this.isLoading = true;
    // Load quick start guide or training support content from CMS
    this.getQuickStartGuide();
    // Load Training Support Elearning Page content from CMS based on Slug
    this.getTrainingSupportElearning();
    // Set page title and metadata
    this.setTitleAndMetaData();
  }

  /**
   * Load eLearning Page content based on the slug.
   */
  getTrainingSupportElearning(): void {
    this.subs.push(
      this.route.data.subscribe((value) => {
        this.slugName = value.slug;
        const elearningPageId = getPageIdBySlug(value.slug);
        this.getTrainingSupportElearningById(elearningPageId);
        this.getTrainingSupportElearningPage();
      })
    );
  }

  /**
   * Subscribe and set the Training Elearning Page for Genesys Cloud ELearning, Genesys DX Elearning
   * and Genesys BeyondCX Elearning specific training page heading and short description body
   * @param id page id of the Training Elearning Page
   */
  getTrainingSupportElearningById(pageId: number): void {
    this.subs.push(
      this.trainingSupportElearningPageService.getStaticPages().subscribe({
        next: (response: StaticPageInterface[]) => {
          this.isLoading = false;
          if (response && response.length > 0) {
            // TODO: This is repetitive in other pages, need to refactor
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });

            if (staticPages && staticPages.length > 0) {
              const filteredStaticPage = staticPages.filter(
                (staticPage) => staticPage.id === pageId
              );
              if (filteredStaticPage && filteredStaticPage.length > 0) {
                this.trainingSupportHeading = staticPages.filter(
                  (staticPage) => staticPage.id === pageId
                )[0];
              }
            }
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Training Support ELearning content completed');
        },
      })
    );
  }

  /**
   * Get Training Support elearning Page response from wordpress service
   */
  getTrainingSupportElearningPage(): void {
    this.subs.push(
      this.trainingSupportElearningPageService.getElearningStatic(this.slugName).subscribe({
        next: (response: StaticPageInterface[]) => {
          this.isLoading = false;
          if (response && response.length > 0) {
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });
            this.trainingSupportElearningPage = staticPages;
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Training Support ELearning content completed');
        },
      })
    );
  }

  /**
   * Get quick start guide response from wordpress service
   */
  getQuickStartGuide(): void {
    this.subs.push(
      this.quickStartGuideService.getQuickStartGuide().subscribe({
        next: (response: StaticPageInterface[]) => {
          this.isLoading = false;
          if (response && response.length > 0) {
            // TODO: This is repetitive in other pages, need to refactor
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });

            // Filter out the parent quick start guide items that have no children if parent id is 0
            this.parentNavItems = staticPages.filter((staticPage) => staticPage?.parent === 0);

            this.childNavItems = staticPages.filter((staticPage) => staticPage?.parent !== 0);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Quick Start Guide content completed');
        },
      })
    );
  }

  /**
   * Set selected Page title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on  page
    this.titleService.setTitle(`${this.slugName} | Genesys Education`);
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Education enrollments, certifications, and recommended learning in one place.',
    });
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
